import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { gql, useQuery, useMutation } from '@apollo/client';
import { Box, Typography, Button } from '@mui/material';

import { selectProject } from '../../../features/project/projectSlice';
import { GlobalModal } from '../../../features/modal/Modal';
import StandardTable from '../../tables/StandardTable';
import { convertAllDates } from '../../../utils/Functions';
import { FlexEnd } from '../../../commonStyles';
import { handleModal } from '../../../features/modal/modalSlice';
import { useAppDispatch } from '../../../app/hooks';
import TimelineForm from './TimelineForm';
import { IProject } from '../../../types/project';
import { IUser } from '../../../types/user';
import { showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
const Timeline = () => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const [modalType, setModalType] = useState<string>('');
  const [changes, setChanges] = useState<IChange[]>([]);
  const [change, setChange] = useState<IChange | null>(null);

  useQuery(GETCHANGES, {
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        project: project._id,
      },
      sort: 'UPDATEDAT',
    },
    onCompleted: (data) => {
      setChanges(data.changeMany);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [createChange] = useMutation(CREATECHANGE, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Change created successfully'));
      storeDispatch(handleModal(false));
      setModalType('');
      setChanges([data.createChange, ...changes]);
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [updateChange] = useMutation(UPDATECHANGE, {
    onCompleted: (data) => {
      let newChanges = changes.map((change: any) => {
        if (change._id === data.changeUpdateById.record._id) {
          return data.changeUpdateById.record;
        } else return change;
      });
      setChanges(newChanges);
      storeDispatch(showSuccessSnackbar('Change updated successfully'));
      storeDispatch(handleModal(false));
      setModalType('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deleteChange] = useMutation(DELETECHANGE, {
    onCompleted: (data) => {
      setChanges(changes.filter((change: any) => change._id !== data.changeRemovedById.record._id));
      storeDispatch(showSuccessSnackbar('Change deleted successfully'));
      storeDispatch(handleModal(false));
      setModalType('');
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [deleteAttachment] = useMutation(DELETEATTACHMENT, {
    onCompleted: (data) => {
      let newChanges = changes.map((change: any) => {
        if (change._id === data.deleteChangeAttachment._id) {
          return data.deleteChangeAttachment;
        } else return change;
      });
      setChanges(newChanges);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const columns = useMemo(() => {
    let columns = [
      {
        Header: 'Description',
        accessor: (rowData: any) => rowData.description,
      },
      {
        Header: 'Unit(s)/Area Affected',
        accessor: (rowData: any) => rowData.unitsAffected,
      },
      {
        Header: 'Change Type',
        accessor: (rowData: any) => rowData.type,
      },
      {
        Header: 'Implemented By',
        accessor: (rowData: any) => (rowData.user ? rowData.user.fullName : 'N/A'),
      },
      {
        Header: 'Implemented Date',
        accessor: (rowData: any) => convertAllDates(new Date(rowData.updatedAt), 'PP'),
      },
      {
        Header: 'Submitted By',
        accessor: (rowData: any) => rowData.submittedBy,
      },
      {
        Header: 'Submitted Date',
        accessor: (rowData: any) => convertAllDates(new Date(rowData.dateSubmitted), 'PP'),
      },
      {
        Header: 'Attachments',
        accessor: (rowData: any) => {
          return (
            <Box>
              {rowData.attachments.map((attachment: any) => {
                return (
                  <Box>
                    <a style={{ color: '#000', textDecoration: 'none' }} href={attachment.getUrl} target="_blank" rel="noopener noreferrer">
                      <strong>{attachment.name}</strong>
                    </a>
                  </Box>
                );
              })}
            </Box>
          );
        },
      },
      {
        Header: 'Edit',
        accessor: (rowData: any) => {
          return (
            <Button
              color="primary"
              variant="contained"
              onClick={() => {
                storeDispatch(handleModal(true));
                setModalType('edit');
                setChange(rowData);
              }}
            >
              Edit
            </Button>
          );
        },
      },
    ];

    return columns;
  }, [changes]);

  const modalView = () => {
    switch (modalType) {
      case 'add':
        return <TimelineForm change={change} handleSubmit={createChange} />;
      case 'edit':
        return <TimelineForm change={change} handleSubmit={updateChange} deleteChange={deleteChange} deleteAttachment={deleteAttachment} />;
    }
  };
  return (
    <Box>
      <GlobalModal>{modalView()}</GlobalModal>
      <Box sx={{ mb: 2 }}>
        <FlexEnd sx={{ mb: 2 }}>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              storeDispatch(handleModal(true));
              setModalType('add');
            }}
          >
            Add Change
          </Button>
        </FlexEnd>
        <Typography sx={{ mb: 2 }} variant={'h2'}>
          <strong>Change Log</strong>
        </Typography>
        <StandardTable columns={columns} data={changes} />
      </Box>
    </Box>
  );
};

export interface IChange {
  _id: string;
  project: IProject;
  user: IUser;
  type: string;
  description: string;
  unitsAffected: string;
  attachments: any[];
  submittedBy: string;
  dateSubmitted: Date;
  createdAt: string;
  updatedAt: string;
}

const CREATECHANGE = gql`
  mutation createChange($record: CreateOneChangeInput!, $files: [Upload]) {
    createChange(record: $record, files: $files) {
      _id
      project {
        _id
      }
      description
      type
      attachments {
        _id
        name
        getUrl
      }
      unitsAffected
      user {
        fullName
      }
      dateSubmitted
      submittedBy
      createdAt
      updatedAt
    }
  }
`;

const UPDATECHANGE = gql`
  mutation changeUpdateById($_id: MongoID!, $record: UpdateByIdChangeInput!) {
    changeUpdateById(_id: $_id, record: $record) {
      record {
        _id
        project {
          _id
        }
        description
        type
        attachments {
          _id
          name
          getUrl
        }
        unitsAffected
        user {
          fullName
        }
        dateSubmitted
        submittedBy
        createdAt
        updatedAt
      }
    }
  }
`;

const DELETECHANGE = gql`
  mutation changeRemoveById($_id: MongoID!) {
    changeRemoveById(_id: $_id) {
      record {
        _id
      }
    }
  }
`;

const GETCHANGES = gql`
  query changeMany($filter: FilterFindManyChangeInput, $sort: SortFindManyChangeInput) {
    changeMany(filter: $filter, sort: $sort) {
      _id
      project {
        _id
      }
      description
      type
      attachments {
        _id
        name
        getUrl
      }
      unitsAffected
      user {
        fullName
      }
      dateSubmitted
      submittedBy
      createdAt
      updatedAt
    }
  }
`;

const DELETEATTACHMENT = gql`
  mutation deleteChangeAttachment($_id: MongoID!, $attachmentId: MongoID!, $deleteFile: Boolean!) {
    deleteChangeAttachment(_id: $_id, attachmentId: $attachmentId, deleteFile: $deleteFile) {
      _id
      project {
        _id
      }
      description
      type
      attachments {
        _id
        name
        getUrl
      }
      unitsAffected
      user {
        fullName
      }
      dateSubmitted
      submittedBy
      createdAt
      updatedAt
    }
  }
`;

export default Timeline;
